import { distance3, shortLabel } from '../engine/snapping';
import { countLayout } from '@mawkeb/shared/layout';
import { formatLength, LENGTH_HINT, parseLength } from '@mawkeb/shared/units';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLayout } from '../state/layout';
import { EYE_HEIGHT_RANGE, useVenue, type ThemePref } from '../state/store';
import { Camera, Close, Show, Trash } from './icons';

export function Counts() {
  const units = useVenue((s) => s.units);
  const setPref = useVenue((s) => s.setPref);
  const shown = useLayout((s) => s.preview ?? s.layout);
  const counts = useMemo(() => countLayout(shown), [shown]);
  return (
    <div className="panel counts" role="status" aria-label="What is placed in the hall">
      <span>
        <b>{counts.chairs}</b> {counts.chairs === 1 ? 'chair' : 'chairs'}
      </span>
      <span>
        <b>{counts.tables}</b> {counts.tables === 1 ? 'table' : 'tables'}
      </span>
      <span className="only-laptop">
        <b>{counts.banners}</b> {counts.banners === 1 ? 'banner' : 'banners'}
      </span>
      <span className="only-laptop">
        <b>{counts.people}</b> people
      </span>
      <span className="units" role="group" aria-label="Units">
        <button type="button" aria-pressed={units === 'imperial'} onClick={() => setPref('units', 'imperial')} title="Feet and inches">
          ft
        </button>
        <button type="button" aria-pressed={units === 'metric'} onClick={() => setPref('units', 'metric')} title="Metres">
          m
        </button>
      </span>
    </div>
  );
}

export function Loading({ onRetry }: { onRetry: () => void }) {
  const load = useVenue((s) => s.load);
  if (load.phase === 'ready' && !load.message) return null;
  if (load.phase === 'error') {
    return (
      <div className="panel loading error" role="alert">
        <span>{load.message ?? 'The hall couldn’t load.'}</span>
        <button type="button" className="secondary" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }
  const total = load.totalBytes || 1;
  const pct = Math.min(100, Math.round((load.bytes / total) * 100));
  const label =
    load.message ??
    (load.phase === 'starting' || load.phase === 'manifest' || load.phase === 'plan'
      ? 'Opening the hall'
      : load.phase === 'coarse'
        ? 'Loading the scan'
        : load.phase === 'streaming'
          ? 'Adding detail'
          : 'Loading');
  return (
    <div className="panel loading" role="status" aria-live="polite">
      <span>{label}</span>
      {load.totalBytes > 0 && (
        <span className="bar" aria-hidden="true">
          <span style={{ width: `${pct}%` }} />
        </span>
      )}
    </div>
  );
}

export function TapePanel() {
  const tool = useVenue((s) => s.tool);
  const units = useVenue((s) => s.units);
  const list = useVenue((s) => s.measurements);
  const pending = useVenue((s) => s.pendingTape);
  const straight = useVenue((s) => s.tapeStraight);
  const set = useVenue((s) => s.set);
  if (tool !== 'tape') return null;
  const coarse = matchMedia('(pointer: coarse)').matches;
  return (
    <aside className="panel side" aria-label="Tape measure">
      <header>
        <h2>Tape</h2>
        <p className="lead" aria-live="polite">
          {pending
            ? `From ${lower(shortLabel(pending.snap))}. Now ${coarse ? 'tap' : 'click'} the other end.`
            : coarse
              ? 'Tap one end, then the other.'
              : 'Click one end, then the other. It snaps to walls, edges and corners.'}
        </p>
        {coarse ? (
          <div className="row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
            <span>Keep it straight</span>
            <button type="button" className="switch" role="switch" aria-checked={straight} aria-label="Keep it straight" onClick={() => set({ tapeStraight: !straight })} />
          </div>
        ) : (
          <p className="hint">Hold Shift to keep it straight along the hall.</p>
        )}
      </header>
      {list.length > 0 && (
        <ol aria-label="Measurements, newest first">
          {[...list].reverse().map((m) => (
            <li key={m.id}>
              <span className="what">
                <span className="val">{formatLength(distance3(m.a, m.b), units)}</span>
                <small>
                  {shortLabel(m.snapA)} to {lower(shortLabel(m.snapB))}
                </small>
              </span>
              <button type="button" className="icon" aria-label="Remove this measurement" onClick={() => set({ measurements: list.filter((x) => x.id !== m.id) })}>
                <Trash />
              </button>
            </li>
          ))}
        </ol>
      )}
      <footer>
        {list.length > 0 && (
          <button type="button" className="secondary" onClick={() => set({ measurements: [], pendingTape: null })}>
            Clear all
          </button>
        )}
        <button type="button" className="secondary" onClick={() => set({ tool: 'none', pendingTape: null })}>
          Done
        </button>
      </footer>
    </aside>
  );
}

function EyeHeightField() {
  const eye = useVenue((s) => s.eyeHeight);
  const units = useVenue((s) => s.units);
  const setPref = useVenue((s) => s.setPref);
  const [text, setText] = useState(formatLength(eye, units, { style: 'ft-in' }));
  const [bad, setBad] = useState<string | null>(null);
  const id = useId();
  useEffect(() => setText(formatLength(eye, units, { style: 'ft-in' })), [eye, units]);
  const commit = () => {
    const m = parseLength(text, units === 'metric' ? 'm' : 'ft');
    if (m === null) return setBad(LENGTH_HINT);
    if (m < EYE_HEIGHT_RANGE[0] || m > EYE_HEIGHT_RANGE[1]) {
      return setBad(`Between ${formatLength(EYE_HEIGHT_RANGE[0], units)} and ${formatLength(EYE_HEIGHT_RANGE[1], units)}`);
    }
    setBad(null);
    setPref('eyeHeight', m);
  };
  return (
    <>
      <div className="row">
        <label htmlFor={id}>Eye height in Walk</label>
        <input
          id={id}
          className="field"
          inputMode="text"
          value={text}
          aria-invalid={!!bad}
          aria-describedby={`${id}-hint`}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
        />
      </div>
      <p id={`${id}-hint`} className={bad ? 'hint bad' : 'hint'}>
        {bad ?? 'Where a standing guest’s eyes are. 5 ft 4 in unless you change it.'}
      </p>
    </>
  );
}

export function Settings({ onReset, layoutActions }: { onReset: () => void; layoutActions?: { label: string; run: () => void }[] }) {
  const open = useVenue((s) => s.menuOpen);
  const themePref = useVenue((s) => s.themePref);
  const units = useVenue((s) => s.units);
  const diagnostics = useVenue((s) => s.diagnostics);
  const manifest = useVenue((s) => s.manifest);
  const editable = useVenue((s) => s.editable);
  const setPref = useVenue((s) => s.setPref);
  const set = useVenue((s) => s.set);
  const layout = useLayout((s) => s.layout);
  const commitLayout = useLayout((s) => s.commit);
  const divider = layout.divider.on;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current && !ref.current.contains(t) && !t.closest('[aria-haspopup="dialog"]')) set({ menuOpen: false });
    };
    addEventListener('pointerdown', close);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => removeEventListener('pointerdown', close);
  }, [open, set]);
  if (!open) return null;
  const themes: { id: ThemePref; label: string }[] = [
    { id: 'system', label: 'Device' },
    { id: 'light', label: 'Light' },
    { id: 'dark', label: 'Dark' },
  ];
  return (
    <div className="panel menu" role="dialog" aria-label="Settings" ref={ref} onKeyDown={(e) => e.key === 'Escape' && set({ menuOpen: false })}>
      {!!layoutActions?.length && (
        <section className="only-phone">
          <h3>This layout</h3>
          {layoutActions.map((a) => (
            <button
              key={a.label}
              type="button"
              className="secondary wide"
              onClick={() => {
                set({ menuOpen: false });
                a.run();
              }}
            >
              {a.label}
            </button>
          ))}
        </section>
      )}
      <section>
        <h3>Look</h3>
        <div className="seg" role="group" aria-label="Light or dark">
          {themes.map((t) => (
            <button key={t.id} type="button" aria-pressed={themePref === t.id} onClick={() => setPref('themePref', t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </section>
      <section>
        <h3>Units</h3>
        <div className="seg" role="group" aria-label="Units">
          <button type="button" aria-pressed={units === 'imperial'} onClick={() => setPref('units', 'imperial')}>
            Feet and inches
          </button>
          <button type="button" aria-pressed={units === 'metric'} onClick={() => setPref('units', 'metric')}>
            Metres
          </button>
        </div>
      </section>
      <section>
        <EyeHeightField />
      </section>
      <section>
        <div className="row">
          <span>Men’s and women’s divider</span>
          <button type="button" className="switch" role="switch" aria-checked={divider} aria-label="Men’s and women’s divider" disabled={!editable} onClick={() => commitLayout({ ...layout, divider: { ...layout.divider, on: !divider } })} />
        </div>
        <div className="row">
          <span>Show performance</span>
          <button type="button" className="switch" role="switch" aria-checked={diagnostics} aria-label="Show performance" onClick={() => set({ diagnostics: !diagnostics })} />
        </div>
        <button type="button" className="secondary wide" onClick={onReset}>
          Back to the starting view
        </button>
      </section>
      <section className="only-laptop">
        <h3>Keys</h3>
        <p className="hint" style={{ marginTop: 0, lineHeight: 1.7 }}>
          1, 2, 3 switch Plan, Orbit and Walk. T tape, C clean view, P screenshot, Esc to cancel. In Walk: W A S D or the arrows, Shift to go faster.
        </p>
      </section>
      <section>
        {manifest && (
          <p className="hint" style={{ margin: 0 }}>
            {manifest.label}. {manifest.standIn ? 'The real scan of the hall replaces it when it’s ready.' : ''}
          </p>
        )}
        <p className="hint" style={{ margin: manifest ? '6px 0 0' : 0 }}>
          This copy of the app was built {new Date(__BUILT__).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}.
        </p>
      </section>
    </div>
  );
}

export function CleanControls({ onScreenshot, busy }: { onScreenshot: () => void; busy: boolean }) {
  const set = useVenue((s) => s.set);
  const [faded, setFaded] = useState(false);
  useEffect(() => {
    let t = setTimeout(() => setFaded(true), 2500);
    const wake = () => {
      setFaded(false);
      clearTimeout(t);
      t = setTimeout(() => setFaded(true), 2500);
    };
    addEventListener('pointermove', wake);
    addEventListener('pointerdown', wake);
    addEventListener('keydown', wake);
    return () => {
      clearTimeout(t);
      removeEventListener('pointermove', wake);
      removeEventListener('pointerdown', wake);
      removeEventListener('keydown', wake);
    };
  }, []);
  return (
    <div className="panel clean-controls" data-faded={faded} onFocus={() => setFaded(false)}>
      <button type="button" className="icon" aria-label="Take a screenshot" title="Screenshot (P)" onClick={onScreenshot} disabled={busy}>
        <Camera />
      </button>
      <button type="button" className="icon" aria-label="Show the controls again" title="Show controls (Esc)" onClick={() => set({ clean: false })}>
        <Show />
      </button>
    </div>
  );
}

export function Toasts() {
  const toasts = useVenue((s) => s.toasts);
  const dismiss = useVenue((s) => s.dismiss);
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`panel toast${t.tone === 'bad' ? ' bad' : ''}`} role={t.tone === 'bad' ? 'alert' : 'status'}>
          <span>{t.text}</span>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action?.run();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button type="button" className="icon" aria-label="Dismiss" onClick={() => dismiss(t.id)} style={{ width: 28, height: 28 }}>
            <Close />
          </button>
        </div>
      ))}
    </div>
  );
}

export function Diagnostics() {
  const on = useVenue((s) => s.diagnostics);
  const q = useVenue((s) => s.quality);
  const load = useVenue((s) => s.load);
  if (!on) return null;
  const t = load.timings;
  const s = (ms?: number) => (ms === undefined ? '…' : `${(ms / 1000).toFixed(2)} s`);
  const mb = (b: number) => `${(b / 1048576).toFixed(1)} MB`;
  return (
    <div className="panel diag" aria-label="Performance">
      <dl>
        <dt>Device</dt>
        <dd>{q.tier}</dd>
        <dt>Graphics</dt>
        <dd title={q.gpu}>{q.gpu.replace(/^ANGLE \(|\)$/g, '').split(',')[1]?.trim() ?? q.gpu}</dd>
        <dt>Frame</dt>
        <dd>{q.frameMs ? `${q.frameMs} ms, ${q.fps} fps` : 'still'}</dd>
        <dt>Resolution</dt>
        <dd>{q.pixelRatio.toFixed(2)}×</dd>
        <dt>Splats</dt>
        <dd>
          {(q.splatsShown / 1e6).toFixed(2)}M of {(q.splatBudget / 1e6).toFixed(2)}M
        </dd>
        <dt>Stepped down</dt>
        <dd>{q.stepDowns}×</dd>
        <dt>App on screen</dt>
        <dd>{s(t.shell)}</dd>
        <dt>Plan drawn</dt>
        <dd>{s(t.plan)}</dd>
        <dt>First scan</dt>
        <dd>{s(t.coarse)}</dd>
        <dt>Hall loaded</dt>
        <dd>{s(t.full)}</dd>
        <dt>Downloaded</dt>
        <dd>
          {mb(load.bytes - load.fromDevice)} ({mb(load.fromDevice)} kept)
        </dd>
      </dl>
    </div>
  );
}

export function WalkHelp() {
  const view = useVenue((s) => s.view);
  const coarse = matchMedia('(pointer: coarse)').matches;
  const [show, setShow] = useState(true);
  useEffect(() => {
    setShow(true);
    if (!coarse) return;
    const t = setTimeout(() => setShow(false), 6000);
    return () => clearTimeout(t);
  }, [view, coarse]);
  if (view !== 'walk' || !show) return null;
  if (coarse) return <div className="panel thumb-hint">Left thumb to walk, drag anywhere else to look. Double-tap the floor to go there.</div>;
  return (
    <div className="panel walk-help only-laptop">
      <kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> or the arrows to walk, drag to look, <kbd>Shift</kbd> to go faster. Double-click the floor to walk there.
    </div>
  );
}

export function NoWebGL() {
  return (
    <div className="message">
      <div className="panel card" role="alert">
        <h1>This browser can’t show the hall in 3D</h1>
        <p>
          The venue app needs <strong>WebGL 2</strong>, which this browser or device doesn’t have turned on.
        </p>
        <p>Open the link in the latest Chrome, Safari, Edge or Firefox. On an older phone, update the system first.</p>
      </div>
    </div>
  );
}

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
